import {CheckboxControl, SelectControl} from '@wordpress/components';
import {__, sprintf} from '@wordpress/i18n';
import {decodeEntities} from '@wordpress/html-entities';
import type {TaxonomyOptions} from './Taxonomy';
import type {SetAttributes} from './Edit';
import type {Taxonomy} from '@wordpress/api/taxonomies';
import {useSelect} from '@wordpress/data';
import {useEffect, useRef} from 'react';
import {CONFIG} from '../../../globals/config';


export type DisplayOptions = {
	display_everywhere: boolean;
	'display_everywhere/parent_category': number;
} & TaxonomyOptions;

type Props = {
	attributes: DisplayOptions;
	setAttributes: SetAttributes;
	type?: Taxonomy<'edit'>;
};

const Display = ( {type, attributes, setAttributes}: Props ) => {
	const taxonomy = attributes.taxonomy ?? 'category';
	const currentTaxonomy = useRef<string>( taxonomy );

	const terms = useSelect( select => {
		const items = select( 'core' ).getEntityRecords( 'taxonomy', taxonomy as 'category', {
			parent: 0,
			orderby: 'name',
			order: 'asc',
			per_page: 100,
		} ) ?? [];

		return items.filter( item => ! CONFIG.categories.excluded.includes( item.id ) );
	}, [ taxonomy ] );

	// Set the parent category to the first available when taxonomy is switched.
	useEffect( () => {
		if ( currentTaxonomy.current === taxonomy ) {
			return;
		}
		currentTaxonomy.current = taxonomy;
		setAttributes( {
			'display_everywhere/parent_category': terms[ 0 ] !== undefined ? terms[ 0 ].id : 0,
		} );
	}, [ setAttributes, taxonomy, terms ] );

	// Set the parent category to the first available category when no category is selected.
	useEffect( () => {
		if ( 0 === attributes[ 'display_everywhere/parent_category' ] && terms[ 0 ] !== undefined ) {
			setAttributes( {
				'display_everywhere/parent_category': terms[ 0 ].id,
			} );
		}
		// eslint-disable-next-line -- Don't want to set the attribute if it changes.
	}, [ terms ] );

	return ( <>
		<CheckboxControl
			label={__( 'Display menu on every page', 'advanced-sidebar-menu' )}
			help={__( 'Display the same menu on every page that has this block.', 'advanced-sidebar-menu' )}
			checked={attributes.display_everywhere}
			onChange={value => {
				setAttributes( {
					display_everywhere: value,
				} );
			}}
			// @ts-expect-error -- Not technically available until WP 6.7.
			__nextHasNoMarginBottom
		/>

		{attributes.display_everywhere && <>
			<SelectControl
				value={attributes[ 'display_everywhere/parent_category' ].toString()}
				/* translators: %s: post type singular label */
				label={sprintf( __( 'Highest level parent %s', 'advanced-sidebar-menu' ), type?.labels?.singular_name.toLowerCase() ?? '' )}
				/* translators: %s: post type singular label */
				help={sprintf( __( 'Menu generates using this %s as the top level item.', 'advanced-sidebar-menu' ), type?.labels?.singular_name.toLowerCase() ?? '' )}
				options={terms.map( page => ( {
					label: decodeEntities( page.name ),
					value: page.id.toString(),
				} ) )}
				onChange={value => {
					setAttributes( {
						'display_everywhere/parent_category': parseInt( value ),
					} );
				}} />
		</>}

	</> );
};

export default Display;
